import React from "react";
import { LuFileText, LuImage, LuLink, LuLock, LuShieldAlert } from "react-icons/lu";
import "./library-share.css";

// TBV-951: pieces shared by the public Library link page (LibraryShareView)
// and the in-app Library item page (TriviaLibraryViewPage), so a visitor who
// may not see an item gets the same access card on either path.

const GENERIC_ERROR_MESSAGE = "Something went wrong. Please try again.";

// What the item is called in the copy ("Access this image") and its file row.
const KIND_COPY = {
  IMAGE: { noun: "image", meta: "Image", Icon: LuImage },
  PDF: { noun: "document", meta: "PDF document", Icon: LuFileText },
  TXT: { noun: "document", meta: "Text document", Icon: LuFileText },
  FILE: { noun: "document", meta: "Document", Icon: LuFileText },
  TEXT: { noun: "document", meta: "Text", Icon: LuFileText },
  WEBPAGE: { noun: "link", meta: "Web link", Icon: LuLink },
};
export const getKindCopy = (kind) => KIND_COPY[kind] || KIND_COPY.FILE;

// "PDF document · 3 pages" — the page count only when the BE could read it.
const getKindMeta = (kind, pageCount) => {
  const meta = getKindCopy(kind).meta;
  if (!Number.isInteger(pageCount) || pageCount < 1) {
    return meta;
  }
  return `${meta} · ${pageCount} ${pageCount === 1 ? "page" : "pages"}`;
};

// The file row above the blurred page (part of the dimmed background).
export const LibraryFileRow = ({ name, kind, pageCount }) => {
  const KindIcon = getKindCopy(kind).Icon;
  return (
    <div className="library_share_file_row">
      <KindIcon className="library_share_file_icon" />
      <div className="library_share_file_text">
        <div className="library_share_file_name">{name}</div>
        <div className="library_share_file_meta">{getKindMeta(kind, pageCount)}</div>
      </div>
    </div>
  );
};

// The full-screen gated layout: the file row + blurred page fill the screen
// behind a white dim layer (which also covers the header), and the access
// card sits centered in the viewport above it.
export const LibraryGateLayout = ({ header, name, kind, pageCount, embedded = false, children }) => (
  <div className={`library_share_page is-gated ${embedded ? "library_share_page_embedded" : ""}`}>
    {header}
    <main className="library_share_stage" aria-hidden="true">
      <div className="library_share_stage_bg">
        {name && <LibraryFileRow name={name} kind={kind} pageCount={pageCount} />}
        <BlurredPagePlaceholder kind={kind} />
      </div>
    </main>
    <div className="library_share_dim" aria-hidden="true" />
    <div className="library_share_gate_center">{children}</div>
  </div>
);

// The API's error helpers (express-response-formatter) send
// { errors: { message } }; older handlers send { message } or { data: { message } }.
export const getApiErrorBody = (error) => {
  const body = error && error.response && error.response.data;
  if (!body) {
    return null;
  }
  if (body.errors && !Array.isArray(body.errors)) {
    return body.errors;
  }
  if (body.data && body.data.message) {
    return body.data;
  }
  return body;
};

export const getApiErrorMessage = (error, fallback = GENERIC_ERROR_MESSAGE) => {
  const body = getApiErrorBody(error);
  return body && typeof body.message === "string" && body.message ? body.message : fallback;
};

// Decorative stand-in for the item behind the access card. It is a generic
// page layout, never the real file: anything sent before the visitor is
// allowed in would be readable with dev tools, however blurred on screen.
export const BlurredPagePlaceholder = ({ kind }) => (
  <div className="library_share_backdrop" aria-hidden="true">
    <div className="library_share_backdrop_top">
      <span className="library_share_ph library_share_ph_brand" />
      <span className="library_share_ph library_share_ph_date" />
    </div>
    <span className="library_share_ph library_share_ph_kicker" />
    <span className="library_share_ph library_share_ph_title" />
    <span className="library_share_ph library_share_ph_line" />
    <span className="library_share_ph library_share_ph_line is-short" />
    <div className={`library_share_ph_media ${kind === "IMAGE" ? "is-image" : ""}`} />
    {kind !== "IMAGE" && (
      <>
        <span className="library_share_ph library_share_ph_heading" />
        <div className="library_share_ph_columns">
          {[0, 1].map((column) => (
            <div key={column}>
              <span className="library_share_ph library_share_ph_subheading" />
              <span className="library_share_ph library_share_ph_line" />
              <span className="library_share_ph library_share_ph_line is-short" />
            </div>
          ))}
        </div>
      </>
    )}
  </div>
);

// The access card with only a notice (no form) — for a signed-in user who may
// not see the item, or an item that is no longer available. `name` / `kind`
// are optional; without a name the file rows are left out.
export const LibraryAccessDenied = ({ name, kind, message, embedded = false }) => {
  const kindCopy = getKindCopy(kind);
  const KindIcon = kindCopy.Icon;
  return (
    <LibraryGateLayout name={name} kind={kind} embedded={embedded}>
      <div className="library_share_gate" role="alert">
        <div className="library_share_gate_intro">
          <span className="library_share_gate_chip">
            <LuLock />
          </span>
          <h4 className="library_share_gate_title">Access this {kindCopy.noun}</h4>
          {name && (
            <div className="library_share_gate_file">
              <KindIcon />
              <span>{name}</span>
            </div>
          )}
        </div>
        <div className="library_share_gate_body">
          <div className="library_share_access_error mb-0">
            <LuShieldAlert />
            <span>{message}</span>
          </div>
        </div>
      </div>
    </LibraryGateLayout>
  );
};
